/** @jsxImportSource @emotion/react */
import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  ReactNode,
} from 'react'
import { App } from 'antd'
import type { UploadChangeParam, UploadFile } from 'antd/es/upload'
import { useSelector } from 'src/hooks/useRedux'
import { getSelfUser } from '@sportninja/common/selectors/users'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import {
  ALLOWED_IMAGE_TYPES,
  MAX_IMAGE_SIZE_MB,
} from '@sportninja/common/constants/imageRequirements'
import { bindActionToPromise } from '@sportninja/common/actions/utils'
import usersActions from '@sportninja/common/actions/users'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
// @ts-ignore
import { store } from '@sportninja/common/store'
import { type UpdatePlayerPayload } from '@sportninja/common/services/player/playerProfile'
import {
  usePlayerProfile,
  useUpdatePlayerProfile,
} from '../hooks/usePlayerProfile'

dayjs.extend(utc)

type CustomError = {
  name_first: string | null
  name_last: string | null
  birth_date: string | null
  phone_number: string | null
  height: string | null
  weight: string | null
}

type EditProfileState = {
  name_first: string
  name_last: string
  birth_date: string
  phone_number: string
  phone_number_country: string
  height: string
  weight: string
  image_url: string
  image_data: string | null
  image_changed: boolean
  delete_image: boolean
  isLoading: boolean
  error: string | null
  fieldErrors: string[]
  customError: CustomError
}

type EditProfileAction =
  | { type: 'SET_FIELD'; field: string; value: unknown }
  | {
      type: 'SET_IMAGE'
      payload: { url: string | null; data: string | null; changed: boolean }
    }
  | { type: 'SET_LOADING'; value: boolean }
  | { type: 'SET_ERROR'; error: string | null }
  | { type: 'SET_FIELD_ERRORS'; errors: string[] }
  | { type: 'SET_CUSTOM_ERROR_BY_FIELD'; field: string; message: string | null }
  | { type: 'CLEAR_CUSTOM_ERROR_BY_FIELD'; field: string }
  | { type: 'CLEAR_ALL_ERRORS' }
  | { type: 'INIT_FROM_USER'; user: any }

const initialCustomError: CustomError = {
  name_first: null,
  name_last: null,
  birth_date: null,
  phone_number: null,
  height: null,
  weight: null,
}

const initialState: EditProfileState = {
  name_first: '',
  name_last: '',
  birth_date: '',
  phone_number: '',
  phone_number_country: '',
  height: '',
  weight: '',
  image_url: '',
  image_data: null,
  image_changed: false,
  delete_image: false,
  isLoading: false,
  error: null,
  fieldErrors: [],
  customError: initialCustomError,
}

function editProfileReducer(
  state: EditProfileState,
  action: EditProfileAction
): EditProfileState {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value }

    case 'SET_IMAGE':
      return {
        ...state,
        image_url: action.payload.url || '',
        image_data: action.payload.data,
        image_changed: action.payload.changed,
        delete_image: action.payload.changed && !action.payload.url,
      }

    case 'SET_LOADING':
      return { ...state, isLoading: action.value }

    case 'SET_ERROR':
      return { ...state, error: action.error }

    case 'SET_FIELD_ERRORS':
      return { ...state, fieldErrors: action.errors }

    case 'SET_CUSTOM_ERROR_BY_FIELD':
      return {
        ...state,
        customError: {
          ...state.customError,
          [action.field]: action.message,
        },
        fieldErrors: action.message
          ? [...new Set([...state.fieldErrors, action.field])]
          : state.fieldErrors,
      }

    case 'CLEAR_CUSTOM_ERROR_BY_FIELD':
      return {
        ...state,
        customError: {
          ...state.customError,
          [action.field]: null,
        },
        fieldErrors: state.fieldErrors.filter((e) => e !== action.field),
      }

    case 'CLEAR_ALL_ERRORS':
      return {
        ...state,
        error: null,
        fieldErrors: [],
        customError: initialCustomError,
      }

    case 'INIT_FROM_USER': {
      const user = action.user
      let birthDateStr = ''
      if (user?.birth_date || user?.birthDate) {
        const raw = user.birth_date || user.birthDate
        const parsed = dayjs.utc(raw)
        if (parsed.isValid()) {
          birthDateStr = parsed.format('YYYY-MM-DD')
        }
      }

      return {
        ...initialState,
        name_first: user?.nameFirst || user?.name_first || '',
        name_last: user?.nameLast || user?.name_last || '',
        birth_date: birthDateStr,
        phone_number: user?.phone_number || user?.phoneNumber || '',
        phone_number_country:
          user?.phone_number_country || user?.phoneNumberCountry || '',
        height:
          user?.height !== null && user?.height !== undefined
            ? String(user.height)
            : '',
        weight:
          user?.weight !== null && user?.weight !== undefined
            ? String(user.weight)
            : '',
        // `getImageThumbnailId` handles the user-shaped image; the player
        // profile exposes `image.full_path`, so fall back to that before
        // `imageId` so player-mode shows the avatar too.
        image_url:
          getImageThumbnailId(user) ||
          user?.image?.full_path ||
          user?.imageId ||
          '',
      }
    }

    default:
      return state
  }
}

type EditProfileContextValue = {
  mode: 'user' | 'player'
  state: EditProfileState
  dispatch: React.Dispatch<EditProfileAction>
  onSubmit: () => Promise<void>
  onCancel: () => void
  setCustomErrorByField: (field: string, message: string | null) => void
  clearCustomErrorByField: (field: string) => void
  beforeUpload: (file: File) => boolean
  handleChange: (info: UploadChangeParam<UploadFile>) => void
}

const EditProfileContext = createContext<EditProfileContextValue | null>(null)

export const useEditProfileContext = () => {
  const context = useContext(EditProfileContext)
  if (!context) {
    throw new Error(
      'useEditProfileContext must be used within EditProfileProvider'
    )
  }
  return context
}

type EditProfileProviderProps = {
  children: ReactNode
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
  mode?: 'user' | 'player'
  playerId?: string
}

export const EditProfileProvider: React.FC<EditProfileProviderProps> = ({
  children,
  isOpen,
  onClose,
  onSuccess,
  mode = 'user',
  playerId,
}) => {
  const [state, dispatch] = useReducer(editProfileReducer, initialState)
  const { message } = App.useApp()
  const selfUser = useSelector((s: any) => getSelfUser(s))
  const { data: playerData } = usePlayerProfile(
    mode === 'player' ? playerId : null
  )
  const updatePlayer = useUpdatePlayerProfile()

  // Initialize the form exactly once per open session. Without this guard the
  // effect would re-run on every `selfUser`/`playerData` object-identity change
  // (e.g. an unrelated Redux update) and wipe whatever the user is typing.
  const initializedRef = React.useRef(false)

  React.useEffect(() => {
    if (!isOpen) {
      // Reset so the NEXT open re-initializes from fresh data.
      initializedRef.current = false
      return
    }
    if (initializedRef.current) {
      return
    }

    const source = mode === 'player' ? playerData : selfUser
    if (!source) {
      // Source not loaded yet (e.g. player profile still fetching). Wait for it
      // to arrive on a later render; the flag stays false so we init then.
      return
    }

    initializedRef.current = true
    dispatch({ type: 'INIT_FROM_USER', user: source })
    dispatch({ type: 'CLEAR_ALL_ERRORS' })

    if (source?.phone_number || source?.phoneNumber) {
      const phone = source.phone_number || source.phoneNumber
      const parsed = parsePhoneNumberFromString(phone)
      if (parsed?.country) {
        dispatch({
          type: 'SET_FIELD',
          field: 'phone_number_country',
          value: parsed.country,
        })
      }
    }
  }, [isOpen, mode, selfUser, playerData])

  const getBase64 = (
    img: File,
    callback: (result: string | ArrayBuffer | null) => void
  ) => {
    const reader = new FileReader()
    reader.addEventListener('load', () => callback(reader.result))
    if (img) {
      reader.readAsDataURL(img)
    }
  }

  const beforeUpload = useCallback(
    (file: File): boolean => {
      const isAllowedType = ALLOWED_IMAGE_TYPES.includes(file.type)
      const isLtMaxSize = file.size / 1024 / 1024 <= MAX_IMAGE_SIZE_MB
      if (!isAllowedType) {
        message.error(
          'Please upload a file in one of the following formats: JPEG, GIF, PNG, AVIF, WebP, TIFF, or HEIC.'
        )
        return false
      }
      if (!isLtMaxSize) {
        message.error(
          'File is too large. Please upload an image smaller than 5MB.'
        )
        return false
      }
      return true
    },
    [message]
  )

  const handleChange = useCallback((info: UploadChangeParam<UploadFile>) => {
    if (info.file.originFileObj) {
      getBase64(info.file.originFileObj, (url) => {
        dispatch({
          type: 'SET_IMAGE',
          payload: {
            url: url as string,
            data: url as string,
            changed: true,
          },
        })
      })
    }
  }, [])

  const setCustomErrorByField = useCallback(
    (field: string, errorMessage: string | null) => {
      dispatch({
        type: 'SET_CUSTOM_ERROR_BY_FIELD',
        field,
        message: errorMessage,
      })
    },
    []
  )

  const clearCustomErrorByField = useCallback((field: string) => {
    dispatch({ type: 'CLEAR_CUSTOM_ERROR_BY_FIELD', field })
  }, [])

  const setCustomErrors = useCallback(
    (invalid_fields: Record<string, string | string[]>) => {
      const normalizeError = (error: string | string[]): string => {
        return Array.isArray(error) ? error.join(', ') : error
      }
      Object.entries(invalid_fields).forEach(([field, error]) => {
        setCustomErrorByField(field, normalizeError(error))
      })
    },
    [setCustomErrorByField]
  )

  const onSubmit = useCallback(async () => {
    dispatch({ type: 'CLEAR_ALL_ERRORS' })

    // Front-end validation
    const errors: string[] = []
    if (!state.name_first?.trim()) {
      dispatch({
        type: 'SET_CUSTOM_ERROR_BY_FIELD',
        field: 'name_first',
        message: 'First name is required',
      })
      errors.push('name_first')
    }
    if (!state.name_last?.trim()) {
      dispatch({
        type: 'SET_CUSTOM_ERROR_BY_FIELD',
        field: 'name_last',
        message: 'Last name is required',
      })
      errors.push('name_last')
    }
    if (errors.length > 0) {
      return
    }

    // Guard the save target before we start: player mode needs a player id,
    // user mode needs the logged-in user. Without these we'd otherwise hit
    // PUT /players/undefined or read `undefined.id`.
    if (mode === 'player' && !playerId) {
      dispatch({
        type: 'SET_ERROR',
        error: 'No player selected to edit.',
      })
      return
    }
    if (mode !== 'player' && !selfUser?.id) {
      dispatch({
        type: 'SET_ERROR',
        error: 'Your account is still loading. Please try again.',
      })
      return
    }

    dispatch({ type: 'SET_LOADING', value: true })

    try {
      const formData: Record<string, unknown> = {
        name_first: state.name_first,
        name_last: state.name_last,
      }

      if (state.birth_date) {
        formData.birth_date = state.birth_date
      }

      if (state.phone_number?.trim().length > 0) {
        formData.phone_number = state.phone_number
        formData.phone_number_country = state.phone_number_country || 'US'
      }

      if (state.height !== '') {
        formData.height = parseFloat(state.height)
      }

      if (state.weight !== '') {
        formData.weight = parseFloat(state.weight)
      }

      // Image handling via saga (skipped in player mode — BE endpoint missing)
      if (mode !== 'player') {
        if (state.image_changed && state.image_data) {
          formData.imageData = state.image_data
        } else if (state.delete_image) {
          formData.delete_image = true
        }
      }

      if (mode === 'player') {
        await updatePlayer.mutateAsync({
          playerId: playerId as string,
          payload: formData as UpdatePlayerPayload,
        })
      } else {
        const updateUser = bindActionToPromise(
          store.dispatch,
          usersActions.update.request
        )
        await updateUser(selfUser.id, formData)
      }

      message.success('Profile updated successfully')
      onSuccess?.()
      onClose()
    } catch (error: unknown) {
      const err = error as {
        invalid_fields?: Record<string, string | string[]>
        message?: string
      }

      if (err.invalid_fields) {
        setCustomErrors(err.invalid_fields)
      } else {
        const errorMessage =
          err.message ||
          (error instanceof Error ? error.message : 'An error occurred')
        dispatch({ type: 'SET_ERROR', error: errorMessage })
      }
    } finally {
      dispatch({ type: 'SET_LOADING', value: false })
    }
  }, [
    state,
    selfUser?.id,
    onClose,
    onSuccess,
    message,
    setCustomErrors,
    mode,
    playerId,
    updatePlayer,
  ])

  const onCancel = useCallback(() => {
    const source = mode === 'player' ? playerData : selfUser
    if (source) {
      dispatch({ type: 'INIT_FROM_USER', user: source })
    }
    dispatch({ type: 'CLEAR_ALL_ERRORS' })
    onClose()
  }, [onClose, mode, selfUser, playerData])

  const value: EditProfileContextValue = {
    mode,
    state,
    dispatch,
    onSubmit,
    onCancel,
    setCustomErrorByField,
    clearCustomErrorByField,
    beforeUpload,
    handleChange,
  }

  return (
    <EditProfileContext.Provider value={value}>
      {children}
    </EditProfileContext.Provider>
  )
}

export default EditProfileContext
